import React, { useState } from 'react'
import Select from 'react-select'
import { Opt, SourceCode } from '../../../constants/carouselOptions'
import formStyles from '../../../styles/forms.module.scss'
import listStyles from '../../../styles/list-table.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import { Button } from '../../Button'
import {
  FilterControl,
  FilterDescriptor,
  descriptorsForSources,
} from './carouselFilterRegistry'
import styles from './CardCarouselEditor.module.scss'

type Filters = Record<string, unknown>

interface CarouselFilterBuilderProps {
  sources: SourceCode[]
  filters: Filters
  onChange: (filters: Filters) => void
}

const hintFor = (d: FilterDescriptor, sources: SourceCode[]): string | undefined =>
  typeof d.hint === 'function' ? d.hint(sources) : d.hint

const hasValue = (v: unknown): boolean => {
  if (v == null) return false
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'string') return v.trim().length > 0
  if (typeof v === 'object') return Object.keys(v as object).length > 0
  return true
}

const StartWindowControl: React.FC<{ value: { relative?: string; value?: number }; onChange: (v: unknown) => void }> = ({ value, onChange }) => {
  const UNIT_OPTIONS: Opt[] = [
    { value: 'next_hours', label: 'Next hours' },
    { value: 'next_days', label: 'Next days' },
  ]
  const unit = value.relative ?? 'next_hours'
  return (
    <div className={styles.startWindow}>
      <Select<Opt, false>
        options={UNIT_OPTIONS}
        value={UNIT_OPTIONS.find(o => o.value === unit) ?? null}
        onChange={(o) => {
          // No number yet → don't store a relative-without-value filter ACE
          // would reject or ignore; wait until the user enters a value.
          const rel = o?.value ?? 'next_hours'
          onChange(value.value == null ? {} : { relative: rel, value: value.value })
        }}
        className={selectStyles.reactSelect}
        classNamePrefix="react-select"
      />
      <input
        type="number"
        min={1}
        value={value.value ?? ''}
        placeholder="e.g. 24"
        onChange={(e) => {
          const n = e.target.value ? parseInt(e.target.value, 10) : undefined
          onChange(n == null || Number.isNaN(n) ? {} : { relative: unit, value: n })
        }}
      />
    </div>
  )
}

type DisplayWindowValue = { relative?: string; value?: number; from?: string; to?: string }

// The display-date counterpart to StartWindowControl. Its units point backwards
// (a catalog rail merchandises content that already exists, where a match rail
// looks ahead), plus a fixed from/to range for a rail pinned to a past event.
// ACE takes either form and relative wins when both are set, so the two are
// offered as a choice rather than side by side.
const DisplayWindowControl: React.FC<{ value: DisplayWindowValue; onChange: (v: unknown) => void }> = ({ value, onChange }) => {
  // ACE also accepts today/yesterday, but a whole-day window says nothing that
  // a rolling one cannot, so they are not offered.
  const UNIT_OPTIONS: Opt[] = [
    { value: 'past_days', label: 'Past days' },
    { value: 'past_hours', label: 'Past hours' },
  ]
  // Which mode is showing is UI state, not a property of the value: a range the
  // user has selected but not yet filled has no from/to to infer it from, so
  // deriving it would snap the control back to rolling on the first click.
  const [mode, setMode] = useState<'relative' | 'range'>(
    value.from != null || value.to != null ? 'range' : 'relative'
  )
  const isRange = mode === 'range'
  const unit = value.relative ?? 'past_days'

  const setDate = (field: 'from' | 'to', raw: string) => {
    const next: DisplayWindowValue = { from: value.from, to: value.to }
    if (raw) next[field] = new Date(raw).toISOString()
    else delete next[field]
    onChange(next.from == null && next.to == null ? undefined : next)
  }

  return (
    <div className={styles.startWindow}>
      <Select<Opt, false>
        options={[{ value: 'relative', label: 'Rolling' }, { value: 'range', label: 'Fixed range' }]}
        value={isRange ? { value: 'range', label: 'Fixed range' } : { value: 'relative', label: 'Rolling' }}
        onChange={(o) => {
          const next = o?.value === 'range' ? 'range' : 'relative'
          setMode(next)
          // Drop whatever the other mode was holding: the two shapes are
          // mutually exclusive to ACE, which lets relative win when both are
          // set, so carrying one across would silently outrank the new mode.
          const stale = next === 'range' ? value.relative != null : (value.from ?? value.to) != null
          if (stale) onChange(undefined)
        }}
        className={selectStyles.reactSelect}
        classNamePrefix="react-select"
      />
      {isRange ? (
        <>
          <input
            type="date"
            aria-label="Display window from"
            value={value.from ? value.from.slice(0, 10) : ''}
            onChange={(e) => setDate('from', e.target.value)}
          />
          <input
            type="date"
            aria-label="Display window to"
            value={value.to ? value.to.slice(0, 10) : ''}
            onChange={(e) => setDate('to', e.target.value)}
          />
        </>
      ) : (
        <>
          <Select<Opt, false>
            options={UNIT_OPTIONS}
            value={UNIT_OPTIONS.find(o => o.value === unit) ?? null}
            onChange={(o) => {
              // No number yet → don't store a relative-without-value filter.
              const rel = o?.value ?? 'past_days'
              onChange(value.value == null ? undefined : { relative: rel, value: value.value })
            }}
            className={selectStyles.reactSelect}
            classNamePrefix="react-select"
          />
          <input
            type="number"
            min={1}
            value={value.value ?? ''}
            placeholder="e.g. 30"
            onChange={(e) => {
              const n = e.target.value ? parseInt(e.target.value, 10) : undefined
              onChange(n == null || Number.isNaN(n) ? undefined : { relative: unit, value: n })
            }}
          />
        </>
      )}
    </div>
  )
}

const ControlField: React.FC<{ control: FilterControl; value: unknown; onChange: (v: unknown) => void }> = ({ control, value, onChange }) => {
  switch (control.kind) {
    case 'enum-multi': {
      const arr = (value as string[]) ?? []
      return (
        <Select<Opt, true>
          isMulti
          options={control.options}
          value={control.options.filter(o => arr.includes(o.value))}
          onChange={(v) => {
            const next = v.map(o => o.value)
            onChange(next.length ? next : undefined)
          }}
          className={selectStyles.reactSelect}
          classNamePrefix="react-select"
        />
      )
    }
    case 'enum-single': {
      return (
        <Select<Opt, false>
          isClearable
          options={control.options}
          value={control.options.find(o => o.value === value) ?? null}
          onChange={(o) => onChange(o?.value ?? undefined)}
          className={selectStyles.reactSelect}
          classNamePrefix="react-select"
        />
      )
    }
    case 'number':
      return (
        <input
          type="number"
          min={control.min}
          placeholder={control.placeholder}
          value={(value as number) ?? ''}
          onChange={(e) => {
            const n = e.target.value ? parseInt(e.target.value, 10) : undefined
            onChange(n == null || Number.isNaN(n) ? undefined : n)
          }}
        />
      )
    case 'lookup': {
      const Comp = control.Component
      return (
        <Comp
          ids={(value as string[]) ?? []}
          onChange={(ids) => onChange(ids.length ? ids : undefined)}
          placeholder={control.placeholder}
        />
      )
    }
    case 'start-window':
      return <StartWindowControl value={(value as { relative?: string; value?: number }) ?? {}} onChange={onChange} />
    case 'display-window':
      return <DisplayWindowControl value={(value as DisplayWindowValue) ?? {}} onChange={onChange} />
  }
}

const CarouselFilterBuilder: React.FC<CarouselFilterBuilderProps> = ({ sources, filters, onChange }) => {
  // Non-core filters the user has revealed via "Add filter" but not yet filled.
  const [revealed, setRevealed] = useState<Set<string>>(new Set())

  const setFilter = (key: string, value: unknown) => {
    const next = { ...filters }
    if (value === undefined) delete next[key]
    else next[key] = value
    onChange(next)
  }

  // Keep a row on screen once its control has been touched. Rows are shown when
  // they hold a value, so a control passing through an empty state — clearing a
  // multi-select, or switching the display window between its two modes — would
  // otherwise vanish mid-edit. This belongs on the interaction, not inside
  // setFilter: removeFilter also calls setFilter, and revealing there would
  // immediately undo the removal.
  const revealFilter = (key: string) =>
    setRevealed(prev => (prev.has(key) ? prev : new Set(prev).add(key)))

  const removeFilter = (d: FilterDescriptor) => {
    setRevealed(prev => { const s = new Set(prev); s.delete(d.key); return s })
    setFilter(d.key, undefined)
  }

  if (sources.length === 0) {
    return <p className={formStyles.hint}>Select at least one source to configure filters.</p>
  }

  const applicable = descriptorsForSources(sources)
  // Core rows anchor the list, so they lead. Without this the two sources read
  // differently for no reason anyone chose: matches declares its core row first
  // and added filters fall below it, while vod's is declared after the shared
  // block, which would put almost everything a merchandiser adds *above* the
  // anchor. Sorting is stable, so registry order still holds within each group.
  const shown = applicable
    .filter(d => d.core || hasValue(filters[d.key]) || revealed.has(d.key))
    .sort((a, b) => Number(b.core ?? false) - Number(a.core ?? false))
  const addable = applicable.filter(d => !shown.includes(d))
  const addOptions: Opt[] = addable.map(d => ({ value: d.key, label: d.label }))

  return (
    <div className={styles.filters}>
      <div>
        <div className={`${listStyles.tableHeader} ${styles.filterGrid}`}>
          <div>Filter</div>
          <div>Value</div>
          <div />
        </div>
        <div className={`${listStyles.tableBody} ${listStyles.tableBodyDense}`}>
          {shown.map(d => {
            const hint = hintFor(d, sources)
            return (
            <div key={d.key} className={`${listStyles.tableRow} ${styles.filterGrid}`}>
              <div className={listStyles.cellPrimary}>
                <p className={listStyles.primaryText}>{d.label}</p>
                {hint && <p className={listStyles.secondaryText}>{hint}</p>}
              </div>
              <ControlField
                control={d.control}
                value={filters[d.key]}
                onChange={(v) => { revealFilter(d.key); setFilter(d.key, v) }}
              />
              <div className={listStyles.cellActions}>
                {!d.core && (
                  <Button variant="secondary" size="small" onClick={() => removeFilter(d)} title="Remove filter">✕</Button>
                )}
              </div>
            </div>
            )
          })}
        </div>
      </div>

      {addOptions.length > 0 && (
        <div className={styles.addFilter}>
          <Select<Opt, false>
            options={addOptions}
            value={null}
            placeholder="+ Add filter…"
            onChange={(o) => o && setRevealed(prev => new Set(prev).add(o.value))}
            className={selectStyles.reactSelect}
            classNamePrefix="react-select"
          />
        </div>
      )}
    </div>
  )
}

export default CarouselFilterBuilder
